// Website pixels: the Pixel Studio overlay — extraction mappings, identity/PII
// and event firing on the left, the live tester (real samples, the collected
// payload, captured PII and provider beacons) on the right.

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { Icons } from '@/components/primitives';
import { pixelSamples } from '@/api/resources';
import type {
  ExtractionRule,
  PiiRule,
  PixelApiValue,
  PixelExtractPreviewRequest,
  PixelExtractPreviewResponse,
  PixelPii,
  FiringConfig,
  PixelExtraction,
  WebEventType,
} from '@/api/types';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { useCan } from '@/auth/capabilities';
import { MutationErrorLine } from '@/components/error-panel';
import { RequiredField } from '@/components/required-field';
import type { useRequiredFeedback } from '@/components/required-field';
import {
  DEFAULT_EXTRACTION,
  DEFAULT_FIRING,
  DEFAULT_PII,
  PIXEL_EVENTS,
  pixelDraftRequiredIssues,
} from '@/lib/pixel-draft';
import type { PixelDraft } from '@/lib/pixel-draft';
import {
  BEACON_PROVIDERS,
  CONSENT_MODES,
  FIRING_EVENTS,
  INTERNAL_FIELDS,
  PII_CHANNELS,
  PII_FIELDS,
  TRANSFORMS,
  WHEN_OPTIONS,
  agoLabel,
  enumeratePaths,
  maskValue,
  oneOf,
  truncHash,
} from '@/lib/pixel-studio-model';

const stCell: CSSProperties = { padding: '5px 8px', borderBottom: '1px solid var(--line-2)', fontSize: 12 };
const stTh: CSSProperties = { textAlign: 'left', fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-3)', padding: '5px 8px' };

export interface PixelStudioProps {
  open: boolean;
  pixel: PixelApiValue;
  draft: PixelDraft;
  set: (patch: Partial<PixelDraft>) => void;
  canManage: boolean;
  onClose: () => void;
  onSave?: (before: PixelApiValue, draft: PixelDraft) => void;
  onAttemptSave: () => void;
  saving: boolean;
  requiredFeedback: ReturnType<typeof useRequiredFeedback>;
}

// Pixel Studio — full-width overlay: config (mappings · identity/PII · firing)
// on the left, a live tester (real samples + full collected payload + beacons)
// on the right. Edits the same `draft`/`set` the drawer owns.
export function PixelStudio({
  open,
  pixel,
  draft,
  set,
  canManage,
  onClose,
  onSave,
  onAttemptSave,
  saving,
  requiredFeedback,
}: PixelStudioProps) {
  const ext = draft?.extraction || DEFAULT_EXTRACTION;
  const piiCfg = draft?.pii || DEFAULT_PII;
  const firing = draft?.firing || DEFAULT_FIRING;
  const setExt = (patch: Partial<PixelExtraction>) => set({ extraction: { ...ext, ...patch } });
  const setPii = (patch: Partial<PixelPii>) => set({ pii: { ...piiCfg, ...patch } });
  const setFiring = (patch: Partial<FiringConfig>) => set({ firing: { ...firing, ...patch } });

  const rules = ext.rules || [];
  const piiRules = ext.pii_rules || [];
  const updateRule = (i: number, p: Partial<ExtractionRule>) => {
    const nextRules = rules.map((r, j) => j === i ? { ...r, ...p } : r);
    setExt({ rules: nextRules });
    const key = `pixel-mapping-rule-${i}`;
    if (!pixelDraftRequiredIssues({
      ...draft,
      extraction: { ...ext, rules: nextRules },
    }).some((issue) => issue.key === key)) {
      requiredFeedback?.clear(key);
    }
  };
  const addRule = () => setExt({ rules: [...rules, { source: '', target: 'production', transform: 'none', event_type: null }] });
  const removeRule = (i: number) => {
    setExt({ rules: rules.filter((_, j) => j !== i) });
    Object.keys(requiredFeedback?.messages || {})
      .filter((key) => key.startsWith('pixel-mapping-rule-'))
      .forEach((key) => requiredFeedback.clear(key));
  };
  const updatePiiRule = (i: number, p: Partial<PiiRule>) => {
    const nextRules = piiRules.map((r, j) => j === i ? { ...r, ...p } : r);
    setExt({ pii_rules: nextRules });
    const key = `pixel-pii-rule-${i}`;
    if (!pixelDraftRequiredIssues({
      ...draft,
      extraction: { ...ext, pii_rules: nextRules },
    }).some((issue) => issue.key === key)) {
      requiredFeedback?.clear(key);
    }
  };
  const addPiiRule = () => setExt({ pii_rules: [...piiRules, { source: '', field: 'email', event_type: null }] });
  const removePiiRule = (i: number) => {
    setExt({ pii_rules: piiRules.filter((_, j) => j !== i) });
    Object.keys(requiredFeedback?.messages || {})
      .filter((key) => key.startsWith('pixel-pii-rule-'))
      .forEach((key) => requiredFeedback.clear(key));
  };

  const [evType, setEvType] = useState<WebEventType>('Purchase');
  const [sampleText, setSampleText] = useState('{}');
  const [touched, setTouched] = useState(false);
  const [parseErr, setParseErr] = useState('');
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  // Real samples for THIS pixel — fetched live (never KV-cached: may hold PII)
  // and refetched every 5 min so a fresher event replaces the sample.
  //
  // Gated on `pixel_samples_view`, which is narrower than the `pixels_view` that
  // opens this screen: a reporter can inspect a pixel's setup but not what it
  // captured. Without this the query would 403 every 5 minutes for that role —
  // a permission denial logged as a server error, which is exactly the noise the
  // health chip was reporting.
  const canSeeSamples = useCan('pixel_samples_view');
  const samplesQ = useQuery(
    pixelSamples(pixel?.id ?? '', { enabled: !!open && !!pixel?.id && canSeeSamples }),
  );
  const samples = samplesQ.data?.samples ?? [];
  const evSamples = samples.filter((s) => s.event_type === evType);
  const samplesKey = samples.map((s) => s.web_event_id).join(',');
  const newest = evSamples[0];

  useEffect(() => { setTouched(false); }, [evType]);
  useEffect(() => {
    if (touched) return;
    if (evSamples.length) setSampleText(JSON.stringify(evSamples[0]?.vars ?? {}, null, 2));
    else setSampleText('{}');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evType, samplesKey, touched]);

  const parsedVars = useMemo((): unknown => { try { return JSON.parse(sampleText); } catch { return null; } }, [sampleText]);
  const paths = useMemo(() => (parsedVars ? Array.from(new Set(enumeratePaths(parsedVars))) : []), [parsedVars]);
  const dlId = `pl-paths-${pixel?.id || 'x'}`;

  const testM = useRegisteredMutation({ noDataEffect: true, mutationFn: (body: PixelExtractPreviewRequest) => api.post<PixelExtractPreviewResponse>('/pixels/extract-preview', { body }) });
  const runTest = () => {
    setParseErr('');
    let vars: unknown;
    try { vars = JSON.parse(sampleText); } catch (e) { setParseErr('Invalid JSON: ' + ((e instanceof Error && e.message) || 'parse error')); return; }
    // Send the effective config; strip transient UI-only keys (`_metaOn`, …).
    const { _metaOn, _ttdOn, _funnelOn, ...cleanPixel } = draft;
    testM.mutate({ pixel: { ...cleanPixel, extraction: ext, pii: piiCfg, firing }, vars, event_type: evType });
  };
  const result = testM.data;

  if (!open) return null;

  const firingRows: ReadonlyArray<readonly [keyof FiringConfig, string, boolean]> = [
    ['meta', 'Meta', !!draft.meta_pixel_id], ['ttd', 'TTD', !!draft.ttd_adv_id],
  ];

  const ruleRow = (r: ExtractionRule | PiiRule, i: number, isPii: boolean) => {
    const fieldKey = `pixel-${isPii ? 'pii' : 'mapping'}-rule-${i}`;
    return (
    <RequiredField
      key={i}
      fieldKey={fieldKey}
      message={requiredFeedback?.messages?.[fieldKey]}
      attempt={requiredFeedback?.attempt ?? 0}
      surface
      style={{ marginBottom: 4 }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input data-validation-focus list={dlId} className="input mono" style={{ flex: 2, minWidth: 0, fontSize: 12 }}
        placeholder={isPii ? 'customer.email' : 'ecommerce.items[0].item_id'} value={r.source} disabled={!canManage}
        onChange={(e) => (isPii ? updatePiiRule : updateRule)(i, { source: e.target.value })} />
      {/* `'field' in r` is `isPii`: only PII rules carry a field. */}
      {'field' in r ? (
        <select className="input" style={{ width: 96, fontSize: 12 }} value={r.field} disabled={!canManage} onChange={(e) => { const field = oneOf(PII_FIELDS, e.target.value); if (field) updatePiiRule(i, { field }); }}>
          {PII_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      ) : (
        <>
          <select className="input" style={{ width: 92, fontSize: 12 }} value={r.target} disabled={!canManage} onChange={(e) => { const target = oneOf(INTERNAL_FIELDS, e.target.value); if (target) updateRule(i, { target }); }}>
            {INTERNAL_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select className="input" style={{ width: 66, fontSize: 12 }} value={r.transform || 'none'} disabled={!canManage} onChange={(e) => { const transform = oneOf(TRANSFORMS, e.target.value); if (transform) updateRule(i, { transform }); }}>
            {TRANSFORMS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </>
      )}
      <select className="input" style={{ width: 100, fontSize: 12 }} value={r.event_type || ''} disabled={!canManage}
        onChange={(e) => (isPii ? updatePiiRule : updateRule)(i, { event_type: oneOf(PIXEL_EVENTS, e.target.value) ?? null })}>
        {WHEN_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
      </select>
      {canManage && <button className="btn btn-sm" onClick={() => (isPii ? removePiiRule : removeRule)(i)} title="Remove">×</button>}
      </div>
    </RequiredField>
    );
  };

  return (
    <>
      {/* Native top-layer nesting keeps the Studio above its parent drawer. */}

      <ResponsiveOverlay label="Pixel Studio" kind="form" onClose={onClose} className="pixel-studio" style={{ position: 'fixed', inset: 16, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 13, fontWeight: 700 }}>Pixel Studio · {pixel?.id}</div>
            <div className="muted" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft?.description || '—'}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
            {canManage && (
              <button className="btn btn-sm btn-primary" disabled={saving} onClick={onAttemptSave}>
                {Icons.check} {saving ? 'Saving…' : 'Save changes'}
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={onClose}>Done</button>
          </div>
        </div>

        {/* Body: two panes */}
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {/* ── LEFT: config ─────────────────────────────────────────── */}
          <div style={{ minWidth: 0, overflow: 'auto', padding: '14px 16px' }}>
            <datalist id={dlId}>{paths.map((p) => <option key={p} value={p} />)}</datalist>

            <div className="field-lbl mb-2 flex between items-center">
              <span>Data extraction</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>
                <input type="checkbox" checked={ext.auto_harvest !== false} disabled={!canManage} onChange={(e) => setExt({ auto_harvest: e.target.checked })} /> Auto-harvest dataLayer
              </label>
            </div>
            <div className="field-hint" style={{ marginBottom: 6 }}>
              Map data-layer paths to internal fields — type to autocomplete from the loaded sample. <code className="mono">show</code> maps into <code className="mono">production_id</code>. <code className="mono">source_reference</code> matches a production's EncorePro house code (e.g. <code className="mono">AATTAA</code>). <b>When</b> scopes a rule to one event (or all). Rules run server-side at collect time — install snippets live in the pixel drawer.
            </div>
            {rules.map((r, i) => ruleRow(r, i, false))}
            {canManage && <button className="btn btn-sm" onClick={addRule} style={{ marginTop: 2 }}>+ Add mapping</button>}

            <div className="field-lbl mb-2" style={{ marginTop: 16 }}>Identity &amp; PII</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 6 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!piiCfg.enabled} disabled={!canManage} onChange={(e) => setPii({ enabled: e.target.checked })} /> Harvest PII
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!piiCfg.advanced_matching} disabled={!canManage || !piiCfg.enabled} onChange={(e) => setPii({ advanced_matching: e.target.checked })} /> Advanced matching (hashed → Meta/TTD)
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!piiCfg.resolve_identity} disabled={!canManage || !piiCfg.enabled} onChange={(e) => setPii({ resolve_identity: e.target.checked })} /> Resolve person identity
              </label>
            </div>
            <RequiredField
              fieldKey="pixel-consent-var"
              message={requiredFeedback?.messages?.['pixel-consent-var']}
              attempt={requiredFeedback?.attempt ?? 0}
              surface
              style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}
            >
              <span className="muted" style={{ fontSize: 11 }}>Consent</span>
              <select className="input" style={{ width: 120, fontSize: 12 }} value={piiCfg.consent || 'always'} disabled={!canManage} onChange={(e) => {
                const consent = oneOf(CONSENT_MODES, e.target.value);
                if (consent) setPii({ consent });
                if (e.target.value === 'always' || String(piiCfg.consent_var ?? '').trim()) {
                  requiredFeedback?.clear('pixel-consent-var');
                }
              }}>
                <option value="always">Always</option>
                <option value="opt_in">Opt-in</option>
                <option value="opt_out">Opt-out</option>
              </select>
              {(piiCfg.consent === 'opt_in' || piiCfg.consent === 'opt_out') && (
                <input className="input mono" style={{ flex: 1, minWidth: 0, fontSize: 12 }} placeholder="consent dataLayer path e.g. consent.granted"
                  data-validation-focus
                  value={piiCfg.consent_var || ''} disabled={!canManage} onChange={(e) => {
                    setPii({ consent_var: e.target.value });
                    if (e.target.value.trim()) requiredFeedback?.clear('pixel-consent-var');
                  }} />
              )}
            </RequiredField>
            {piiCfg.enabled && (
              <>
                <div className="field-hint" style={{ marginBottom: 4 }}>PII field mappings (also autodetected from form fields):</div>
                {piiRules.map((r, i) => ruleRow(r, i, true))}
                {canManage && <button className="btn btn-sm" onClick={addPiiRule} style={{ marginTop: 2 }}>+ Add PII field</button>}
              </>
            )}
            <div className="field-hint" style={{ marginTop: 6 }}>Raw PII is sent over TLS to resolve identity, then discarded — only SHA-256 hashes and the resolved person are stored.</div>

            <div className="field-lbl mb-2" style={{ marginTop: 16 }}>Event firing</div>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr><th style={stTh}></th><th style={stTh}>Purchase</th><th style={stTh}>Pageview</th></tr></thead>
              <tbody>
                {firingRows.map(([key, label, provisioned]) => (
                  <tr key={key}>
                    <td style={{ ...stCell, fontWeight: 600, opacity: provisioned ? 1 : 0.5 }}>{label}{!provisioned && <span className="muted" style={{ fontWeight: 400 }}> (no id)</span>}</td>
                    {FIRING_EVENTS.map((ev) => (
                      <td key={ev} style={{ ...stCell, textAlign: 'center' }}>
                        <input type="checkbox" disabled={!canManage || !provisioned} checked={(firing[key]?.[ev]) !== false}
                          onChange={(e) => setFiring({ [key]: { ...(firing[key] || { purchase: true, pageview: true }), [ev]: e.target.checked } })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── RIGHT: live tester ───────────────────────────────────── */}
          <div style={{ minWidth: 0, overflow: 'auto', padding: '14px 16px', borderLeft: '1px solid var(--line)', background: 'var(--bg-sunk)' }}>
            <div className="field-lbl mb-2 flex between items-center">
              <span>Live tester</span>
              <button className="btn btn-sm btn-primary" onClick={runTest} disabled={testM.isPending}>{testM.isPending ? 'Testing…' : 'Test'}</button>
            </div>

            {/* Event + sample picker */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <select className="input" style={{ width: 120, fontSize: 12 }} value={evType} onChange={(e) => { const next = oneOf(PIXEL_EVENTS, e.target.value); if (next) setEvType(next); }}>
                <option value="Purchase">Purchase</option>
                <option value="Pageview">Pageview</option>
              </select>
              {/* Absence for a permission reason must not read as absence of
                  traffic. Without this branch a reporter lands on the red "no
                  live sample is available" below and concludes the pixel has
                  stopped firing. */}
              {!canSeeSamples ? (
                <span className="muted" style={{ fontSize: 11 }}>
                  Live samples require <span className="mono">pixel_samples_view</span> — the editor is using {'{}'}.
                </span>
              ) : samplesQ.isFetching && evSamples.length ? (
                <span className="muted" style={{ fontSize: 11 }}>refreshing · showing {evSamples.length} cached API sample{evSamples.length > 1 ? 's' : ''}</span>
              ) : samplesQ.isFetching ? (
                <span className="muted" style={{ fontSize: 11 }}>loading samples…</span>
              ) : samplesQ.isError && evSamples.length ? (
                <span style={{ fontSize: 11, color: 'var(--warn)' }}>⚠ Refresh failed · showing {evSamples.length} cached API sample{evSamples.length > 1 ? 's' : ''} · newest {agoLabel(newest?.event_at)}</span>
              ) : samplesQ.isError ? (
                <span style={{ fontSize: 11, color: 'var(--bad)' }}>⚠ Live samples could not be loaded. The editor is using {'{}'}.</span>
              ) : evSamples.length ? (
                <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>
                  {evSamples.length} API sample{evSamples.length > 1 ? 's' : ''} · newest {agoLabel(newest?.event_at)}
                </span>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--bad)' }}>⚠ No live {evType} sample is available. The editor is using {'{}'}.</span>
              )}
              <button
                className="btn btn-sm"
                onClick={() => { setTouched(false); samplesQ.refetch(); }}
                disabled={!canSeeSamples}
                title={canSeeSamples ? 'Refetch latest' : 'Requires pixel_samples_view'}
              >↻</button>
            </div>
            {evSamples.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                {evSamples.slice(0, 8).map((s, i) => (
                  <button key={s.web_event_id} className="btn btn-sm"
                    style={{ fontSize: 10.5, opacity: !touched && i === 0 ? 1 : 0.72 }}
                    title={`${s.domain} · ${agoLabel(s.event_at)}`}
                    onClick={() => { setTouched(true); setSampleText(JSON.stringify(s.vars ?? {}, null, 2)); }}>
                    #{i + 1} · {agoLabel(s.event_at)}
                  </button>
                ))}
              </div>
            )}

            <textarea className="input mono" style={{ width: '100%', minHeight: 120, fontSize: 11.5 }} value={sampleText} spellCheck={false}
              onChange={(e) => { setTouched(true); setSampleText(e.target.value); }} placeholder="dataLayer JSON…" />
            {parseErr && <div className="field-hint" style={{ color: 'var(--bad)' }}>{parseErr}</div>}
            {testM.error && <div style={{ marginTop: 6 }}><MutationErrorLine error={testM.error} /></div>}

            {result && (
              <div style={{ marginTop: 10 }}>
                {/* Collected payload */}
                <div className="field-lbl mb-2">Collected payload <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· full CollectRequest (ids/time synthetic)</span></div>
                <pre className="mono" style={{ fontSize: 10.5, maxHeight: 200, overflow: 'auto', background: 'var(--bg-elev)', border: '1px solid var(--line-2)', padding: 8, borderRadius: 'var(--r-sm)', margin: 0 }}>
                  {JSON.stringify(result.payload, null, 2)}
                </pre>

                {/* Resolved mapped fields */}
                <div className="field-lbl mb-2" style={{ marginTop: 12 }}>Resolved fields</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={stTh}>Field</th><th style={stTh}>Value</th></tr></thead>
                  <tbody>
                    {Object.entries(result.mapped || {}).filter(([, v]) => v != null).map(([k, v]) => (
                      <tr key={k}><td style={{ ...stCell, fontFamily: 'var(--font-mono)' }}>{k}</td><td style={{ ...stCell, fontFamily: 'var(--font-mono)' }}>{String(v)}</td></tr>
                    ))}
                    {Object.entries(result.mapped || {}).filter(([, v]) => v != null).length === 0 && (
                      <tr><td style={stCell} colSpan={2}><span className="muted">Nothing mapped from this sample.</span></td></tr>
                    )}
                  </tbody>
                </table>

                {/* Production match — how source_reference resolved (or why it didn't) */}
                {result.production_match && (() => {
                  const pm = result.production_match;
                  const ref = pm.source_reference ?? '';
                  const line = {
                    hit:       { color: 'var(--good)',  text: `→ ${pm.production_name ?? pm.production_id} (${pm.production_id})` },
                    miss:      { color: 'var(--warn)',  text: `no production matches '${ref}'` },
                    ambiguous: { color: 'var(--warn)',  text: `multiple productions share '${ref}'` },
                    skipped:   { color: 'var(--ink-3)', text: 'explicit production_id wins — lookup skipped' },
                  }[pm.outcome];
                  if (!line) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 11.5 }}>
                      <span className="muted mono" style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase' }}>Production match</span>
                      <span className="mono" style={{ color: line.color }}>{line.text}</span>
                    </div>
                  );
                })()}

                {/* Captured PII — every channel, reveal + hash */}
                <div className="field-lbl mb-2" style={{ marginTop: 12 }}>Captured PII <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· click 👁 to reveal</span></div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={stTh}>Channel</th><th style={stTh}>Captured</th><th style={stTh}>Normalized</th><th style={stTh}>Hash (Meta)</th></tr></thead>
                  <tbody>
                    {PII_CHANNELS.map(([k, label]) => {
                      const ch = (result.pii && result.pii[k]) || {};
                      const has = ch.raw != null && ch.raw !== '';
                      const rev = !!revealed[k];
                      return (
                        <tr key={k} style={{ opacity: has ? 1 : 0.5 }}>
                          <td style={{ ...stCell, color: 'var(--chart-3)' }}>{label}</td>
                          <td style={{ ...stCell, fontFamily: 'var(--font-mono)' }}>
                            {has ? (
                              <>
                                {rev ? ch.raw : maskValue(ch.raw)}{' '}
                                <button className="btn btn-sm" style={{ padding: '0 4px' }} onClick={() => setRevealed((s) => ({ ...s, [k]: !s[k] }))} title={rev ? 'Hide' : 'Reveal'}>{rev ? '🙈' : '👁'}</button>
                              </>
                            ) : <span className="muted">— not captured</span>}
                          </td>
                          <td style={{ ...stCell, fontFamily: 'var(--font-mono)' }}>{ch.normalized || (has ? '—' : '')}</td>
                          <td style={{ ...stCell, fontFamily: 'var(--font-mono)' }} title={ch.hash_meta ? `Meta: ${ch.hash_meta}\nTTD:  ${ch.hash_ttd}` : ''}>{truncHash(ch.hash_meta)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Beacons */}
                <div className="field-lbl mb-2" style={{ marginTop: 12 }}>Provider beacons</div>
                {BEACON_PROVIDERS.map((pk) => {
                  const b = result.beacons && result.beacons[pk];
                  const label = pk === 'meta' ? 'Meta' : 'TTD';
                  if (!b) return <div key={pk} className="muted" style={{ fontSize: 11, marginBottom: 4 }}>{label}: not provisioned (no id)</div>;
                  return (
                    <div key={pk} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 2 }}>
                        {label} {b.will_fire ? <span style={{ color: 'var(--good)' }}>● will fire</span> : <span style={{ color: 'var(--ink-3)' }}>○ {b.note || 'off'}</span>}
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {(b.params || []).map(([pkk, pv], j) => (
                            <tr key={j}><td style={{ ...stCell, fontFamily: 'var(--font-mono)', color: 'var(--ink-3)', width: '38%' }}>{pkk}</td><td style={{ ...stCell, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{pv}</td></tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mono" style={{ fontSize: 9.5, wordBreak: 'break-all', color: 'var(--ink-3)', marginTop: 3 }}>{b.url}</div>
                    </div>
                  );
                })}

                {(result.warnings || []).length > 0 && (
                  <div className="field-hint" style={{ marginTop: 4, color: 'var(--bad)' }}>{result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
